import React, { useState } from 'react';
import { StarRating } from '@/components/ui/star-rating';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { setFeedbackScore } from '@/services/feedback';
import CloseIcon from '/public/close-2.svg';
import { useI18n } from '@/hooks/use-i18n';

interface RatingCardProps {
  defaultScore?: number;
  defaultFeedback?: string;
  loading?: boolean;
  onClose?: () => void;
  disabled?: boolean;
  scrollToBottom?: () => void;
  agentRunId: string;
}

const RatingCard: React.FC<RatingCardProps> = ({
  defaultScore = 0,
  defaultFeedback = '',
  loading = false,
  onClose,
  disabled = false,
  scrollToBottom,
  agentRunId,
}) => {
  const { t } = useI18n(['components', 'common']);
  const [score, setScore] = useState<number>(defaultScore);
  const [feedback, setFeedback] = useState<string>(defaultFeedback);
  const [submitting, setSubmitting] = useState(false);

  const STAR_LABELS = t(
    'components:ratingCard.starLabels',
  ) as unknown as string[];

  const handleSubmit = async () => {
    if (disabled || loading || submitting) return;
    setSubmitting(true);
    await setFeedbackScore(agentRunId, score, feedback);
    setSubmitting(false);
    toast.success(t('components:ratingCard.thankYou'), {
      position: 'top-center',
    });
    onClose?.();
  };

  const handleClose = () => {
    setScore(defaultScore);
    setFeedback(defaultFeedback);
  };

  const handleScoreChange = (_score: number) => {
    setScore(_score);
    if (score === 0 && _score > 0) {
      setTimeout(() => {
        scrollToBottom?.();
      }, 100);
    }
  };

  return score <= 0 ? (
    <div className="w-full bg-white flex items-center gap-2">
      <span className="text-sm font-normal text-[#BFBFBF]">
        {t('components:ratingCard.howDoYouFeel')}
      </span>
      <div className="inline-block">
        <StarRating
          value={score}
          onChange={handleScoreChange}
          labels={null}
          size={18}
          disabled={disabled || loading || submitting}
        />
      </div>
    </div>
  ) : (
    <div
      className={cn(
        'w-full max-w-[800px] bg-white rounded-[12px] border-[1px] border-[#E5E5E5] pt-3 pb-4',
        score > 0 && 'bg-[#F4F4F5]',
      )}
    >
      <div className="relative px-3">
        {/* 标题 */}
        <div className="text-sm font-semibold text-[#3D3D3D] mb-1">
          {t('components:ratingCard.rateTask')}
        </div>

        {/* 关闭按钮 */}
        <button
          className="absolute -top-1 right-2 p-1 rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
          onClick={handleClose}
          aria-label={t('common:close')}
          tabIndex={0}
        >
          <CloseIcon className="w-4 h-4 text-[#212121]" />
        </button>

        {/* 星级评分 */}
        <StarRating
          value={score}
          onChange={handleScoreChange}
          labels={score > 0 ? STAR_LABELS : []}
          size={24}
          disabled={disabled || loading || submitting}
        />
      </div>

      {score > 0 && (
        <>
          {/* 分割线 */}
          <div className="h-[1px] bg-[#E5E5E5] w-full mt-[10px]" />

          {/* 反馈输入框 */}
          <div className="px-3 pt-3">
            <div className="text-xs font-normal text-[#3D3D3D] mb-2">
              {t('components:ratingCard.otherFeedback')}
            </div>
            <textarea
              className="w-full h-[88px] rounded-[8px] border border-[#E6E7EB] bg-[#FAFAFA] p-3 text-sm resize-none outline-none focus:border-[#8378FA] transition-colors placeholder:text-[#BFBFBF] mb-[14px] disabled:opacity-60 disabled:cursor-not-allowed"
              placeholder={t('components:ratingCard.feedbackPlaceholder')}
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              disabled={disabled || loading || submitting}
              maxLength={300}
            />

            {/* 提交按钮 */}
            <div className="flex justify-end">
              <Button
                className="py-[6px] px-[16px] rounded-[8px] text-white text-[13px] font-medium disabled:opacity-60 disabled:cursor-not-allowed transition-all"
                onClick={handleSubmit}
                disabled={disabled || loading || submitting || score === 0}
              >
                {loading || submitting
                  ? t('components:ratingCard.submitting')
                  : t('common:submit')}
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default RatingCard;
